import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from "react";
import { AppService } from "../services/appService";
import { UserDto, EnabledFunctionsDto, OrderDto, RegisterDraftDto, DictDto, ListingPlanDto, AppConfigDto } from "../types";

const defaultRegisterDraft: RegisterDraftDto = {
  lastName: "",
  firstName: "",
  userName: "",
  email: "",
  phone: "",
  countryPrefix: "+40",
  password: "",
  repeatPassword: "",
  seller: false,
  agreedToTerms: false,
};

interface AuthContextType {
  currentUser: UserDto | null;
  setCurrentUser: React.Dispatch<React.SetStateAction<UserDto | null>>;
  isAuthenticated: boolean;
  currentLang: "ro" | "en";
  setCurrentLang: React.Dispatch<React.SetStateAction<"ro" | "en">>;
  currentLanguageDictionary: DictDto[];
  romanianDictionary: DictDto[];
  englishDictionary: DictDto[];
  enabledMenuFunctions: EnabledFunctionsDto[];
  enabledHomeCardFunctions: EnabledFunctionsDto[];
  isAuthLoading: boolean;
  aircraftCommissionRate: number;
  rentalCommissionRate: number;
  partCommissionRate: number;
  experienceCommissionRate: number;
  aircraftListingsCount: number;
  aircraftRentalsCount: number;
  aircraftPartsCount: number;
  experiencesCount: number;
  setAircraftListingsCount: React.Dispatch<React.SetStateAction<number>>;
  setAircraftRentalsCount: React.Dispatch<React.SetStateAction<number>>;
  setAircraftPartsCount: React.Dispatch<React.SetStateAction<number>>;
  setExperiencesCount: React.Dispatch<React.SetStateAction<number>>;
  refreshPublicCounts: () => Promise<void>;
  login: (identifier: string, password: string) => Promise<boolean>;
  register: (firstName: string, lastName: string, userName: string, email: string, phone: string, password: string, seller: boolean, agreedToTerms: boolean) => Promise<boolean>;
  logout: () => Promise<boolean>;
  syncSystemCommissionRates: () => Promise<number>;
  listingPlans: ListingPlanDto[];
  syncListingPlans: () => Promise<void>;
  refreshUser: () => Promise<void>;
  myOrders: OrderDto[] | null;
  setMyOrders: React.Dispatch<React.SetStateAction<OrderDto[] | null>>;
  getMyOrders: (forceRefresh?: boolean) => Promise<OrderDto[]>;
  getDictValue: (key: string) => string;
  getDictJson: <T>(key: string, defaultValue: T) => T;
  isAppDataLoading: boolean;
  isMaintenanceBlock: boolean;
  isMaintenanceDev: boolean;
  appConfigMap: Map<string, AppConfigDto> | null;
  registerDraft: RegisterDraftDto;
  setRegisterDraft: React.Dispatch<React.SetStateAction<RegisterDraftDto>>;
  clearRegisterDraft: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<UserDto | null>(null);
  const [romanianDictionary, setRomanianDictionary] = useState<DictDto[]>([]);
  const [englishDictionary, setEnglishDictionary] = useState<DictDto[]>([]);
  const [currentLang, setCurrentLang] = useState<"ro" | "en">("ro");
  const [appConfigMap, setAppConfigMap] = useState<Map<string, AppConfigDto> | null>(null);
  const [isMaintenanceBlock, setIsMaintenanceBlock] = useState<boolean>(false);
  const [isMaintenanceDev, setIsMaintenanceDev] = useState<boolean>(false);
  const currentLanguageDictionary = React.useMemo(() => {
    return currentLang === "en" ? englishDictionary : romanianDictionary;
  }, [currentLang, romanianDictionary, englishDictionary]);
  const [enabledMenuFunctions, setEnabledMenuFunctions] = useState<EnabledFunctionsDto[]>([]);
  const [enabledHomeCardFunctions, setEnabledHomeCardFunctions] = useState<EnabledFunctionsDto[]>([]);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  const [aircraftCommissionRate, setAircraftCommissionRate] = useState<number>(0.0);
  const [rentalCommissionRate, setRentalCommissionRate] = useState<number>(0.0);
  const [partCommissionRate, setPartCommissionRate] = useState<number>(0.0);
  const [experienceCommissionRate, setExperienceCommissionRate] = useState<number>(0.0);
  const [listingPlans, setListingPlans] = useState<ListingPlanDto[]>([]);

  const [aircraftListingsCount, setAircraftListingsCount] = useState<number>(0);
  const [aircraftRentalsCount, setAircraftRentalsCount] = useState<number>(0);
  const [aircraftPartsCount, setAircraftPartsCount] = useState<number>(0);
  const [experiencesCount, setExperiencesCount] = useState<number>(0);

  const [myOrders, setMyOrders] = useState<OrderDto[] | null>(null);
  const [registerDraft, setRegisterDraft] = useState<RegisterDraftDto>(defaultRegisterDraft);

  const clearRegisterDraft = useCallback(() => {
    setRegisterDraft(defaultRegisterDraft);
  }, []);

  const [isAppDataLoading, setIsAppDataLoading] = useState(false);

  const myOrdersPromiseRef = useRef<Promise<OrderDto[] | any> | null>(null);

  const refreshPublicCounts = useCallback(async (): Promise<void> => {
    try {
      const [listingsCount, rentalsCount, partsCount, expsCount] = await Promise.all([
        AppService.getAircraftListingsCount(),
        AppService.getAircraftRentalsCount(),
        AppService.getAircraftPartsCount(),
        AppService.getExperiencesCount()
      ]);

      setAircraftListingsCount(listingsCount || 0);
      setAircraftRentalsCount(rentalsCount || 0);
      setAircraftPartsCount(partsCount || 0);
      setExperiencesCount(expsCount || 0);
    } catch (err) {
      console.error("Failed to load public listing counts:", err);
    }
  }, []);

  const syncListingPlans = React.useCallback(async (): Promise<void> => {
    try {
      const plans = await AppService.getListingPlans();
      if (plans && Array.isArray(plans)) {
        setListingPlans(plans);
      }
    } catch (error) {
      console.error("Failed to fetch listing plans", error);
    }
  }, []);

  const refreshUser = async () => {
    const response = await AppService.init();

    if (response?.payload?.userStatus) {
      setCurrentUser({
        userName: response.payload.userName,
        firstName: response.payload.firstName,
        lastName: response.payload.lastName,
        email: response.payload.email,
        phone: response.payload.phone,
        seller: response.payload.seller,
        iban: response.payload.iban,
        vatId: response.payload.vatId,
        companyName: response.payload.companyName,
        memberFrom: response.payload.memberFrom,
        chargingModel: response.payload.chargingModel,
        planName: response.payload.planName,
      });
    }
  };

  const syncSystemCommissionRates = React.useCallback(async (temporaryModel?: string): Promise<number> => {
    try {
      const activeModel = temporaryModel || currentUser?.chargingModel;

      if (activeModel === "BY_LISTING_PLAN") {
        setAircraftCommissionRate(0.0);
        setRentalCommissionRate(0.0);
        setPartCommissionRate(0.0);
        setExperienceCommissionRate(0.0);
        return 0.0;
      }

      const rates = await AppService.getCommissionRates();

      if (!rates) {
        setAircraftCommissionRate(0.0);
        setRentalCommissionRate(0.0);
        setPartCommissionRate(0.0);
        setExperienceCommissionRate(0.0);
        return 0.0;
      }

      const activePackage = Array.isArray(rates) ? rates[0] : rates;

      if (activePackage) {
        setAircraftCommissionRate(activePackage.aircraftListingRate != null ? activePackage.aircraftListingRate : 0.0);
        setRentalCommissionRate(activePackage.aircraftRentalRate != null ? activePackage.aircraftRentalRate : 0.0);
        setPartCommissionRate(activePackage.aircraftPartRate != null ? activePackage.aircraftPartRate : 0.0);
        setExperienceCommissionRate(activePackage.experienceRate != null ? activePackage.experienceRate : 0.0);

        return activePackage.aircraftListingRate || 0.0;
      }
      return 0.0;
    } catch (error) {
      console.error("Error synchronizing commission rates:", error);
      setAircraftCommissionRate(0.0);
      setRentalCommissionRate(0.0);
      setPartCommissionRate(0.0);
      setExperienceCommissionRate(0.0);
      return 0.0;
    }
  }, [currentUser?.chargingModel]);

  const getMyOrders = useCallback(async (forceRefresh = false): Promise<OrderDto[]> => {
    if (!forceRefresh && myOrders !== null) return myOrders;
    if (myOrdersPromiseRef.current) return myOrdersPromiseRef.current;

    const performFetch = async () => {
      try {
        const result = await AppService.getMyOrders();
        const data = Array.isArray(result) ? result : [];
        setMyOrders(data);
        return data;
      } catch (err) {
        console.error("Error fetching orders:", err);
        return [];
      } finally {
        myOrdersPromiseRef.current = null;
      }
    };
    myOrdersPromiseRef.current = performFetch();
    return myOrdersPromiseRef.current;
  }, [myOrders]);

  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    let isMounted = true;

    const initAuth = async () => {
      try {
        const response = await AppService.init();
        if (!isMounted) return;

        if (response?.payload?.userStatus) {
          setCurrentUser({
            userName: response.payload.userName,
            firstName: response.payload.firstName,
            lastName: response.payload.lastName,
            email: response.payload.email,
            phone: response.payload.phone,
            seller: response.payload.seller,
            iban: response.payload.iban,
            vatId: response.payload.vatId,
            companyName: response.payload.companyName,
            memberFrom: response.payload.memberFrom,
            chargingModel: response.payload.chargingModel,
            planName: response.payload.planName,
          });
        } else {
          setCurrentUser(null);
        }

        const dictData = await AppService.getDictionary();
        if (dictData && Array.isArray(dictData) && isMounted) {
          setRomanianDictionary(dictData.filter((item: any) => item.language === "ro"));
          setEnglishDictionary(dictData.filter((item: any) => item.language === "en"));
        }

        const appConfigMap = await AppService.getAppConfigMap();
        if (appConfigMap && isMounted) {
          setAppConfigMap(appConfigMap);

          const maintenanceBlockConfig = appConfigMap.get("app.maintenance.block");
          if (maintenanceBlockConfig && maintenanceBlockConfig.keyValue === "true") {
            setIsMaintenanceBlock(true);
          } else {
            const maintenanceDevConfig = appConfigMap.get("app.maintenance.dev");
            const isMaintenanceDevActive = maintenanceDevConfig && maintenanceDevConfig.keyValue === "true";
            setIsMaintenanceDev(isMaintenanceDevActive);
          }
        }

        const enabledFunctionsResponse = await AppService.enabledFunctions();
        if (enabledFunctionsResponse && isMounted) {
          setEnabledMenuFunctions(enabledFunctionsResponse.filter(item => item.type === "Menu"));
          setEnabledHomeCardFunctions(enabledFunctionsResponse.filter(item => item.type === "HomeCard"));
        }

        if (isMounted) {
          await Promise.all([
            syncSystemCommissionRates(),
            syncListingPlans(),
            refreshPublicCounts()
          ]);
        }

        if (isMounted && response?.payload?.userStatus) {
          await Promise.all([
            getMyOrders()
          ]);
        }

      } catch (err) {
        console.error("Auth and Data initialization failed", err);
      } finally {
        if (isMounted) {
          setIsAuthLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (identifier: string, password: string): Promise<boolean> => {
    const isEmail = identifier.includes("@");
    const dto = { userName: isEmail ? null : identifier, email: isEmail ? identifier : null, password };
    const response = await AppService.login(dto);

    if (response?.payload || response?.cmd === "SHOW_LOGOUT") {
      setCurrentUser({
        userName: response.payload.userName,
        firstName: response.payload.firstName,
        lastName: response.payload.lastName,
        email: response.payload.email,
        phone: response.payload.phone,
        seller: response.payload.seller,
        iban: response.payload.iban,
        vatId: response.payload.vatId,
        companyName: response.payload.companyName,
        memberFrom: response.payload.memberFrom,
        chargingModel: response.payload.chargingModel,
        planName: response.payload.planName,
      });
      await Promise.all([
        syncSystemCommissionRates(),
        syncListingPlans(),
        refreshPublicCounts(),
        getMyOrders()
      ]);
      return true;
    }

    return false;
  };

  const register = async (firstName: string, lastName: string, userName: string, email: string, phone: string, password: string, seller: boolean, agreedToTerms: boolean): Promise<boolean> => {
    const dto = { firstName, lastName, userName, email, phone, encryptedPassword: password, seller, termsConditions: agreedToTerms };
    const response = await AppService.register(dto);
    if (!response || !response.messages) return false;
    return response.messages.some((msg: any) => msg.type === "SUCCESS");
  };

  const logout = async () => {
    const success = await AppService.logout();
    if (success) {
      setCurrentUser(null);
      setMyOrders(null);

      try {
        await AppService.init();
        await refreshPublicCounts();
      } catch (err) {
        console.error("Failed to re-initialize app session after logout:", err);
      }
    }
    return success;
  };

  const getDictValue = useCallback((key: string): string => {
    const entry = currentLanguageDictionary.find(item => item.dictKey === key);
    return entry?.dictValue || "";
  }, [currentLanguageDictionary]);

  const getDictJson = useCallback(<T,>(key: string, defaultValue: T): T => {
    const entry = currentLanguageDictionary.find(item => item.dictKey === key);
    if (!entry || !entry.dictValue) return defaultValue;
    try {
      return JSON.parse(entry.dictValue) as T;
    } catch (e) {
      console.error(`Failed to parse JSON for key: "${key}"`, e);
      return defaultValue;
    }
  }, [currentLanguageDictionary]);

  return (
    <AuthContext.Provider value={{
      currentUser,
      setCurrentUser,
      isAuthenticated: !!currentUser,
      login,
      register,
      logout,
      romanianDictionary,
      englishDictionary,
      enabledMenuFunctions,
      enabledHomeCardFunctions,
      isAuthLoading,
      aircraftCommissionRate,
      rentalCommissionRate,
      partCommissionRate,
      experienceCommissionRate,
      syncSystemCommissionRates,
      listingPlans,
      syncListingPlans,
      refreshUser,
      aircraftListingsCount,
      aircraftRentalsCount,
      aircraftPartsCount,
      experiencesCount,
      setAircraftListingsCount,
      setAircraftRentalsCount,
      setAircraftPartsCount,
      setExperiencesCount,
      refreshPublicCounts,
      isAppDataLoading,
      getDictValue,
      getDictJson,
      currentLang,
      setCurrentLang,
      currentLanguageDictionary,
      isMaintenanceBlock,
      isMaintenanceDev,
      myOrders,
      setMyOrders,
      getMyOrders,
      appConfigMap,
      registerDraft,
      setRegisterDraft,
      clearRegisterDraft,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};